import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { botTokenApi } from 'src/services/bot-tokens/api'
import { botTokenQueries, BotTokenCreated } from 'src/services/bot-tokens'
import { BOT_PERMISSION_GROUPS } from './bot-permission-groups'
import type { BotPermissionGroup } from './bot-permission-groups'
import { cn } from 'src/utils/style'

function BotPermissionGroupRow({
  group,
  selectedPermissions,
  onToggle,
}: {
  group: BotPermissionGroup
  selectedPermissions: Set<string>
  onToggle: (codename: string) => void
}) {
  return (
    <div
      className="rounded-md border border-border bg-surface-hover p-3"
      data-testid={`bot-permission-group-${group.module}`}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {group.label}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {group.access && (
          <label className="flex items-center gap-1.5 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={selectedPermissions.has(group.access.codename)}
              onChange={() => onToggle(group.access!.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`bot-perm-${group.access.codename}`}
            />
            {group.access.label}
          </label>
        )}
        {group.permissions.map((perm) => (
          <label
            key={perm.codename}
            className="flex items-center gap-1.5 text-sm text-text-primary"
          >
            <input
              type="checkbox"
              checked={selectedPermissions.has(perm.codename)}
              onChange={() => onToggle(perm.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`bot-perm-${perm.codename}`}
            />
            {perm.label}
          </label>
        ))}
      </div>
    </div>
  )
}

interface CreateBotModalProps {
  isOpen: boolean
  onClose: () => void
  orgId: string
  /** Called with the created token payload so the parent can reveal the secret. */
  onCreated: (created: BotTokenCreated) => void
}

export const CreateBotModal: React.FC<CreateBotModalProps> = ({
  isOpen,
  onClose,
  orgId,
  onCreated,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set())
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setName('')
      setSelectedPermissions(new Set())
      setApiError(null)
    }
  }, [isOpen])

  const togglePermission = useCallback((codename: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev)
      if (next.has(codename)) {
        next.delete(codename)
        // Deselecting a module's access clears its action permissions too.
        const group = BOT_PERMISSION_GROUPS.find((g) => g.access?.codename === codename)
        if (group) {
          for (const perm of group.permissions) next.delete(perm.codename)
        }
      } else {
        next.add(codename)
        // Selecting an action auto-selects the module access permission.
        const group = BOT_PERMISSION_GROUPS.find((g) =>
          g.permissions.some((p) => p.codename === codename),
        )
        if (group?.access) next.add(group.access.codename)
      }
      return next
    })
  }, [])

  const { mutate: createBot, isPending } = useMutation({
    mutationFn: () =>
      botTokenApi.csc.createBotToken({
        name: name.trim(),
        organization: orgId,
        permissions: Array.from(selectedPermissions),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: botTokenQueries.all() })
      onCreated(created)
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.permissions?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('botTokens.createError')
      setApiError(message)
    },
  })

  const isValid = name.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)
    if (!isValid) return
    createBot()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('botTokens.createBot')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Input
            label={t('common.name')}
            placeholder={t('botTokens.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            data-testid="bot-name-input"
          />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-medium text-text-primary">
            {t('botTokens.permissions')}
          </h4>
          <div className={cn(['space-y-3'])}>
            {BOT_PERMISSION_GROUPS.map((group) => (
              <BotPermissionGroupRow
                key={group.module}
                group={group}
                selectedPermissions={selectedPermissions}
                onToggle={togglePermission}
              />
            ))}
          </div>
        </div>

        {apiError && (
          <p className="text-sm text-error" data-testid="bot-form-error">
            {apiError}
          </p>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isPending}
            disabled={!isValid}
            data-testid="bot-form-submit"
          >
            {t('botTokens.createBot')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
